import { Injectable } from '@angular/core';
import {
  GrantTypeEnum,
  IAccountResponseError,
  ScopeEnum,
} from '../../account.data';
import { IKeyValue } from '../../../app/store';
import { HttpErrorResponse } from '@angular/common/http';
import { trim } from 'lodash';
import {
  ACCEPT_VERSION_HEADER,
  ACCESS_TOKEN_KEY,
  CLIENT_HEADER,
  CLIENT_ID,
  CLIENT_SECRET,
  CREATE_FLAG_KEY,
  IAccountLoginRequest,
  IAccountLoginRequestHeaders,
  IAccountLoginRequestPayload,
  IAccountRegistrationRequest,
  IAccountRegistrationRequestPayload,
  IChangePasswordTokenRequestPayload,
  ICompanyLoginRequestPayload,
  IReferringCompanyRequestQueryParameter,
  ISetPasswordRequestPayload,
  ISetPasswordTokenRequestPayload,
  IUpdatePasswordRequestPayload,
  LOCALE_HEADER,
  REFRESH_TOKEN_KEY,
} from './account-service.data';
import { CONTENT_TYPE_HEADER } from 'i2g-ng-utils';

@Injectable()
export class AccountBuilder {
  constructor() {}

  buildReferringCompanyQueryParameters(
    referral: string
  ): IReferringCompanyRequestQueryParameter {
    return {
      referral,
    };
  }

  buildAccountRegistrationRequest(
    grantType: GrantTypeEnum,
    email: string,
    token: string,
    locale: string,
    referralCode?: string
  ): IAccountRegistrationRequest {
    return {
      email,
      grantType,
      locale,
      referralCode,
      token,
    };
  }

  buildAccountRegistrationRequestPayload(
    request: IAccountRegistrationRequest
  ): IAccountRegistrationRequestPayload {
    return {
      email: request.email,
      token: request.token,
      locale: request.locale,
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      grant_type: request.grantType,
      referral_code: trim(request.referralCode) || undefined,
      scope: ScopeEnum.offline_access,
    };
  }

  buildAccountResponseError(
    httpErrorResponse: HttpErrorResponse
  ): IAccountResponseError {
    const errorPayload = httpErrorResponse.error;
    const { code, description } = errorPayload.error;

    return {
      code,
      description,
    };
  }

  buildAccountCookies(
    accessToken: string,
    refreshToken: string,
    createFlag: boolean = false
  ): IKeyValue[] {
    const cookies = [
      {
        key: ACCESS_TOKEN_KEY,
        value: accessToken,
      },
      {
        key: REFRESH_TOKEN_KEY,
        value: refreshToken,
      },
    ];

    if (createFlag) {
      cookies.push({
        key: CREATE_FLAG_KEY,
        value: 'Y',
      });
    }

    return cookies;
  }

  buildAccountLoginRequestHeaders(
    locale: string,
    version: string
  ): IAccountLoginRequestHeaders {
    return {
      [CONTENT_TYPE_HEADER]: 'application/x-www-form-urlencoded',
      [ACCEPT_VERSION_HEADER]: '3.0.0',
      [CLIENT_HEADER]: `Web/${version}`,
      [LOCALE_HEADER]: locale,
    };
  }

  buildAccountLoginRequest(
    grantType: GrantTypeEnum,
    email: string,
    token: string,
    locale: string
  ): IAccountLoginRequest {
    return {
      email,
      grantType,
      token,
      locale,
    };
  }

  buildLoginByChallengePayload(
    challengeId: string
  ): IAccountLoginRequestPayload {
    return {
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      grant_type: GrantTypeEnum.verification_challenge,
      challenge_id: challengeId,
    };
  }

  buildAccountLoginRequestPayload(
    request: IAccountLoginRequest
  ): IAccountLoginRequestPayload {
    return {
      username: request.email,
      password: request.token,
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      grant_type: request.grantType,
      scope: [ScopeEnum.offline_access, ScopeEnum.read, ScopeEnum.write].join(
        ' '
      ),
      sign_up: false,
    };
  }

  buildAccountSocialLoginRequestPayload(
    request: IAccountLoginRequest
  ): IAccountLoginRequestPayload {
    return {
      username: request.email,
      token: request.token,
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      grant_type: request.grantType,
      scope: [ScopeEnum.offline_access, ScopeEnum.read, ScopeEnum.write].join(
        ' '
      ),
      sign_up: false,
    };
  }

  buildCompanyLoginRequestPayload(
    grantType: GrantTypeEnum,
    accountId: string,
    refreshToken: string
  ): ICompanyLoginRequestPayload {
    return {
      account_id: accountId,
      refresh_token: refreshToken,
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      grant_type: grantType,
    };
  }

  buildSetPasswordRequestPayload(
    email: string,
    locale: string
  ): ISetPasswordRequestPayload {
    return {
      email,
      locale,
    };
  }

  buildValidateSetPasswordTokenRequestPayload(
    token: string,
    locale: string
  ): ISetPasswordTokenRequestPayload {
    return {
      token,
      locale,
    };
  }

  buildUpdatePasswordRequestPayload(
    userId: string,
    token: string,
    password: string,
    locale: string
  ): IUpdatePasswordRequestPayload {
    return {
      user_id: userId,
      new_password: password,
      token,
      locale,
    };
  }

  buildChangeEmailTokenRequestPayload(
    token: string,
    locale: string
  ): IChangePasswordTokenRequestPayload {
    return {
      token,
      locale,
    };
  }
}
